import { EmptyState } from '@platform/ui';
import { ChevronRight, Lock } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';

import { GROUP_LABELS, MODULES, type ModuleDef, canAccess } from '../lib/modules';
import { useMe } from '../lib/queries';
import { BRAND_NAME } from './brand';
import { MODULE_ICONS } from './module-icons';

/** Pone "<título> · Metamindia" en la pestaña del navegador. */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · ${BRAND_NAME}`;
  }, [title]);
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  useDocumentTitle(title);
  const mod = MODULES.find((m) => m.label === title);
  const Icon = mod ? MODULE_ICONS[mod.id] : undefined;
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 lg:mb-8">
      <div className="flex min-w-0 max-w-2xl items-start gap-4">
        {Icon ? (
          <span
            aria-hidden
            className="mt-1 hidden size-12 shrink-0 items-center justify-center rounded-2xl bg-card text-highlight shadow-card ring-1 ring-border sm:flex"
          >
            <Icon className="size-[1.35rem]" strokeWidth={1.75} />
          </span>
        ) : null}
        <div className="min-w-0">
          {mod ? (
            <nav aria-label="Ubicación" className="mb-1 text-xs text-muted-foreground">
              <ol className="flex items-center gap-1.5">
                <li>{GROUP_LABELS[mod.group]}</li>
                <li aria-hidden>
                  <ChevronRight className="size-3" />
                </li>
                <li aria-current="page" className="font-medium text-foreground/80">
                  {mod.label}
                </li>
              </ol>
            </nav>
          ) : null}
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-foreground sm:text-[1.75rem]">
            {title}
          </h1>
          {description ? (
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function NoAccess() {
  return (
    <EmptyState
      icon={<Lock />}
      title="Sin acceso"
      description="Tu usuario no tiene permiso para ver esta sección. Si crees que es un error, pídeselo a quien administra tu empresa."
    />
  );
}

/** Guard de ruta: si el usuario no tiene el permiso, muestra "Sin acceso" (la API igual lo rechaza). */
export function Guard({
  requires,
  children,
}: {
  requires: ModuleDef['requires'];
  children: ReactNode;
}) {
  const me = useMe();
  if (!canAccess(me, requires)) return <NoAccess />;
  return <>{children}</>;
}
